// src/pages/markets/board/board/ProfileBar.tsx
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PublishIcon from '@mui/icons-material/Publish';
import PublicIcon from '@mui/icons-material/Public';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { MAX_USER_PROFILES, type BoardProfile, type ProfileRef } from '../state/profileTypes';
import { ProfilePlaque } from './ProfilePlaque';
import { hoverScrollbar } from '../../../../styles/hoverScrollbar';
import { toolbarButtonSx } from '../../../../components/common/page/toolbarStyles';

export interface ProfileBarProps {
    shared: BoardProfile[];
    personal: BoardProfile[];
    active: ProfileRef | null;
    dirty: boolean;
    atCap: boolean;
    isAdmin: boolean;
    onSelect: (ref: ProfileRef) => void;
    onReset: () => void;
    onSave: () => void;
    onCreate: (name: string) => void;
    onRename: (id: string, name: string) => void;
    onRequestDelete: (ref: ProfileRef) => void;
    onRequestPromote: (id: string) => void;
    /** Admins only: whether the shared row can be dragged into a new order. */
    canOrderShared: boolean;
    /** An admin has dragged the shared row and not yet saved it. */
    sharedOrderPending: boolean;
    onReorderPersonal: (ids: string[]) => void;
    onReorderShared: (ids: string[]) => void;
    /** Phone: 44 px targets and text of at least 12 px. Same functions, same order. */
    touch?: boolean;
}

const SCROLL_STEP_PX = 200;
const TOUCH_TARGET = 44;

const SHARED_SAVE_TITLE =
    'Shared profiles cannot be overwritten — use + to keep these settings as your own.';
const ORDER_SAVE_TITLE = 'Save the shared profile order for everyone.';

const GROUP_SX = { display: 'flex', alignItems: 'center', gap: 0.75 } as const;

const touchButtonSx = { height: TOUCH_TARGET, minWidth: TOUCH_TARGET, px: 1.5, fontSize: '14px' } as const;

export function ProfileBar({
    shared,
    personal,
    active,
    dirty,
    atCap,
    isAdmin,
    onSelect,
    onReset,
    onSave,
    onCreate,
    onRename,
    onRequestDelete,
    onRequestPromote,
    canOrderShared,
    sharedOrderPending,
    onReorderPersonal,
    onReorderShared,
    touch,
}: ProfileBarProps) {
    const rowRef = useRef<HTMLDivElement | null>(null);
    const [overflowing, setOverflowing] = useState(false);
    // null = nothing being named; { id: null } = a new profile; { id } = a rename.
    const [naming, setNaming] = useState<{ id: string | null; initial: string } | null>(null);

    // Measured rather than assumed: where the row overflows depends on how many
    // profiles there are and how long their names run.
    useLayoutEffect(() => {
        const el = rowRef.current;
        if (!el) return;
        const measure = () => setOverflowing(el.scrollWidth > el.clientWidth);
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [shared, personal]);

    const scrollRow = useCallback((delta: number) => {
        rowRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
    }, []);

    // A chip only ever moves within its own list: dropping a personal chip among
    // the shared ones would read as a promotion, which has its own dialog.
    const handleDragEnd = useCallback(
        (result: DropResult) => {
            const { source, destination } = result;
            if (!destination || destination.droppableId !== source.droppableId) return;
            if (destination.index === source.index) return;

            const list = source.droppableId === 'shared' ? shared : personal;
            const ids = list.map(p => p.id);
            const [moved] = ids.splice(source.index, 1);
            if (moved === undefined) return;
            ids.splice(destination.index, 0, moved);

            if (source.droppableId === 'shared') onReorderShared(ids);
            else onReorderPersonal(ids);
        },
        [shared, personal, onReorderShared, onReorderPersonal],
    );

    // The arrows are a mouse's way along a row with no visible scrollbar; a
    // finger swipes the row, and two 44 px arrows would leave a phone's row
    // about 100 px for its chips.
    const showArrows = overflowing && !touch;

    const activeIsShared = active?.scope === 'shared';
    // Gated on the same permission that produced the drag, so the row can never be
    // talked into offering a write the reader is not allowed to make.
    const orderPending = sharedOrderPending && canOrderShared;
    const canSave = orderPending || (dirty && active !== null && !activeIsShared);
    const saveTitle = orderPending
        ? ORDER_SAVE_TITLE
        : activeIsShared
          ? SHARED_SAVE_TITLE
          : '';

    const chip = (p: BoardProfile, scope: 'user' | 'shared') => {
        const selected = active?.scope === scope && active.id === p.id;
        const canRemove = scope === 'user' || isAdmin;
        const canPromote = scope === 'user' && isAdmin;
        const select = () => onSelect({ scope, id: p.id });
        return (
            <Box
                key={`${scope}:${p.id}`}
                // On touch the whole chip is the place to tap: its padding, the
                // globe and the gap would be dead if only the name took the tap.
                // The remove and promote buttons are inside it and keep their
                // own taps.
                onClick={
                    touch
                        ? event => {
                              if (!(event.target as Element).closest('.profile-actions'))
                                  select();
                          }
                        : undefined
                }
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    height: touch ? TOUCH_TARGET : 26,
                    // On touch the name carries its own padding on the right;
                    // the chip takes the tap (onClick above) wherever it lands.
                    px: touch ? 0 : 1,
                    ...(touch && { pl: 1.5 }),
                    borderRadius: touch ? '8px' : '4px',
                    flexShrink: 0,
                    fontSize: touch ? '14px' : '11px',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    color: selected ? 'text.primary' : 'text.secondary',
                    fontWeight: 600,
                    '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
                    // Faded rather than display:none, so the controls stay keyboard- and screen-reader-reachable.
                    '&:hover .profile-actions': { opacity: 1, pointerEvents: 'auto' },
                }}
            >
                {scope === 'shared' && <PublicIcon sx={{ fontSize: touch ? 16 : 11 }} />}
                <Box
                    component="span"
                    sx={
                        touch
                            ? {
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  alignSelf: 'stretch',
                                  minWidth: TOUCH_TARGET,
                                  px: 0.5,
                              }
                            : undefined
                    }
                    onClick={touch ? undefined : select}
                    onDoubleClick={
                        scope === 'user' ? () => setNaming({ id: p.id, initial: p.name }) : undefined
                    }
                >
                    {p.name}
                </Box>
                <Box
                    className="profile-actions"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        opacity: 0,
                        pointerEvents: 'none',
                        transition: 'opacity 120ms',
                        '&:focus-within': { opacity: 1, pointerEvents: 'auto' },
                        // A finger has no hover to reveal them: they stay on the
                        // chip that is selected, and take no room on the others.
                        ...(touch && {
                            overflow: 'hidden',
                            ...(selected
                                ? { opacity: 1, pointerEvents: 'auto' }
                                : { width: 0 }),
                            '&:focus-within': { opacity: 1, pointerEvents: 'auto', width: 'auto' },
                        }),
                    }}
                >
                    {canPromote && (
                        <IconButton
                            size="small"
                            aria-label={`Promote ${p.name}`}
                            onClick={() => onRequestPromote(p.id)}
                            sx={{ p: 0, color: 'primary.main', ...(touch && { width: TOUCH_TARGET, height: TOUCH_TARGET }) }}
                        >
                            <PublishIcon sx={{ fontSize: touch ? 20 : 12 }} />
                        </IconButton>
                    )}
                    {canRemove && (
                        <IconButton
                            size="small"
                            aria-label={`Remove ${p.name}`}
                            onClick={() => onRequestDelete({ scope, id: p.id })}
                            sx={{ p: 0, ...(touch && { width: TOUCH_TARGET, height: TOUCH_TARGET }) }}
                        >
                            <CloseIcon sx={{ fontSize: touch ? 20 : 12 }} />
                        </IconButton>
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
                minWidth: 0,
            }}
        >
            {showArrows && (
                <IconButton
                    size="small"
                    aria-label="Scroll profiles left"
                    onClick={() => scrollRow(-SCROLL_STEP_PX)}
                    sx={{ p: 0.25, flexShrink: 0 }}
                >
                    <ChevronLeftIcon sx={{ fontSize: 16 }} />
                </IconButton>
            )}
            <Box
                ref={rowRef}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: touch ? 1 : 0.75,
                    ...hoverScrollbar,
                    // The row is one chip tall, so only the horizontal axis may scroll.
                    overflowX: 'auto',
                    overflowY: 'hidden',
                }}
            >
                <DragDropContext onDragEnd={handleDragEnd}>
                    {canOrderShared ? (
                        <Droppable
                            droppableId="shared"
                            type="shared"
                            direction="horizontal"
                        >
                            {dropProvided => (
                                <Box
                                    ref={dropProvided.innerRef}
                                    {...dropProvided.droppableProps}
                                    data-testid="profile-drop-shared"
                                    sx={GROUP_SX}
                                >
                                    {shared.map((p, index) => (
                                        <Draggable
                                            key={p.id}
                                            draggableId={`shared:${p.id}`}
                                            index={index}
                                        >
                                            {dragProvided => (
                                                <Box
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    {...dragProvided.dragHandleProps}
                                                >
                                                    <Tooltip title={p.description ?? ''}>
                                                        {chip(p, 'shared')}
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </Draggable>
                                    ))}
                                    {dropProvided.placeholder}
                                </Box>
                            )}
                        </Droppable>
                    ) : (
                        shared.map(p => (
                            <Tooltip key={p.id} title={p.description ?? ''}>
                                {chip(p, 'shared')}
                            </Tooltip>
                        ))
                    )}
                    {shared.length > 0 && personal.length > 0 && (
                        <Box sx={{ width: '1px', height: 16, bgcolor: 'divider', flexShrink: 0 }} />
                    )}
                    <Droppable
                        droppableId="personal"
                        type="personal"
                        direction="horizontal"
                    >
                        {dropProvided => (
                            <Box
                                ref={dropProvided.innerRef}
                                {...dropProvided.droppableProps}
                                data-testid="profile-drop-personal"
                                sx={GROUP_SX}
                            >
                                {personal.map((p, index) => (
                                    <Draggable key={p.id} draggableId={`user:${p.id}`} index={index}>
                                        {dragProvided => (
                                            <Box
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                {...dragProvided.dragHandleProps}
                                            >
                                                {naming?.id === p.id ? (
                                                    <ProfilePlaque
                                                        touch={touch}
                                                        initial={naming.initial}
                                                        taken={personal
                                                            .filter(x => x.id !== p.id)
                                                            .map(x => x.name)}
                                                        onCommit={name => {
                                                            setNaming(null);
                                                            onRename(p.id, name);
                                                        }}
                                                        onCancel={() => setNaming(null)}
                                                    />
                                                ) : (
                                                    chip(p, 'user')
                                                )}
                                            </Box>
                                        )}
                                    </Draggable>
                                ))}
                                {dropProvided.placeholder}
                            </Box>
                        )}
                    </Droppable>
                    {naming?.id === null && (
                        <ProfilePlaque
                            touch={touch}
                            initial=""
                            taken={personal.map(p => p.name)}
                            onCommit={name => {
                                setNaming(null);
                                onCreate(name);
                            }}
                            onCancel={() => setNaming(null)}
                        />
                    )}
                    <Tooltip
                        title={
                            atCap
                                ? `${MAX_USER_PROFILES} profiles is the limit — delete one to make room.`
                                : 'New profile'
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                aria-label="New profile"
                                disabled={atCap}
                                onClick={() => setNaming({ id: null, initial: '' })}
                                sx={touch ? { p: 0, width: TOUCH_TARGET, height: TOUCH_TARGET, flexShrink: 0 } : { p: 0.25, flexShrink: 0 }}
                            >
                                <AddIcon sx={{ fontSize: touch ? 22 : 14 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </DragDropContext>
            </Box>
            {showArrows && (
                <IconButton
                    size="small"
                    aria-label="Scroll profiles right"
                    onClick={() => scrollRow(SCROLL_STEP_PX)}
                    sx={{ p: 0.25, flexShrink: 0 }}
                >
                    <ChevronRightIcon sx={{ fontSize: 16 }} />
                </IconButton>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                <Button
                    size="small"
                    disabled={active === null && !orderPending}
                    onClick={onReset}
                    sx={touch ? { ...toolbarButtonSx, ...touchButtonSx } : toolbarButtonSx}
                >
                    Reset
                </Button>
                <Tooltip title={saveTitle}>
                    <span>
                        <Button
                            size="small"
                            variant="contained"
                            aria-label="Save profile"
                            disabled={!canSave}
                            onClick={onSave}
                            sx={touch ? { ...toolbarButtonSx, ...touchButtonSx } : toolbarButtonSx}
                        >
                            Save
                        </Button>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
}
